import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { motion } from "framer-motion";
import {
  ArrowRight, Buildings, UsersThree, ChartLineUp, Crown,
  Target, Eye, Rocket, Handshake,
  CheckCircle, CalendarCheck, ShieldCheck, Lightning
} from "@phosphor-icons/react";

const LOGO_URL = "/bch-logo.png";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/uz62qvb6_1%20variation.png",
  discussion: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/kvv97gq2_4%20variation.png",
  handshake: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/z38x9i2u_2%20variation.png",
  conference: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/1q4conde_3%20variation.png",
  collab: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/zfaycjjc_5%20variation.png",
};

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const scaleUp = { hidden: { opacity: 0, scale: 0.92 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

export default function AboutPage() {

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: "#FAFAF8", fontFamily: "'Manrope', sans-serif", color: "#1a1a1a" }} data-testid="about-page">
      <SEO title="About" description="Learn about BCH Networking's vision, mission, founder Kamal Agarwal, core team, and chapter-based structure. Discover how 500+ business owners across India connect, collaborate, and grow together." path="/about" />

      {/* HERO — Full-width image with overlay text */}
      <section className="relative pt-20 md:pt-24 overflow-hidden" data-testid="about-hero">
        <div className="relative h-[50vh] md:h-[55vh] min-h-[380px]">
          <img src={IMAGES.hero} alt="Business professionals networking" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.55))" }} />
          <div className="relative z-10 h-full flex items-center justify-center">
            <motion.div variants={stagger} initial="hidden" animate="visible" className="text-center px-6">
              <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-4 font-semibold" style={{ color: "#d4a73a" }}>Our Story</motion.p>
              <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-6xl font-medium tracking-tighter mb-4 sm:mb-5 leading-[1.08]" style={{ fontFamily: "'Outfit', sans-serif", color: "#fff" }}>
                The <span style={{ color: "#d4a73a" }}>BCH</span> Philosophy
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                A structured approach to business networking, built for <span className="font-semibold text-[#d4a73a]">measurable growth</span>.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT IS BCH — split with image */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="what-is-bch">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
                What is <span style={{ color: "#1a8a6d" }}>BCH</span>?
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base leading-relaxed mb-6" style={{ color: "#666" }}>
                BCH Networking is a professional networking platform designed to help business owners grow through structured collaboration.
              </motion.p>
              <motion.blockquote variants={fadeUp} className="relative py-5 px-6 rounded-2xl mb-6"
                style={{ background: "#f2f2ee", borderLeft: "3px solid #d4a73a" }}>
                <p className="text-lg font-medium italic" style={{ color: "#333", fontFamily: "'Outfit', sans-serif" }}>
                  &ldquo;When the right people connect consistently in a structured environment, real business growth happens.&rdquo;
                </p>
              </motion.blockquote>
              <motion.p variants={fadeUp} className="text-base leading-relaxed" style={{ color: "#666" }}>
                We combine offline networking discipline with digital tracking systems to ensure every interaction contributes to measurable outcomes.
              </motion.p>
            </div>
            <motion.div variants={scaleUp} className="rounded-3xl overflow-hidden shadow-xl aspect-[16/10] sm:aspect-[4/3]">
              <img src={IMAGES.discussion} alt="Business discussion" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-10 md:py-14" style={{ background: "#f2f2ee" }} data-testid="vision-mission">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="grid md:grid-cols-2 gap-6">
            <motion.div variants={fadeUp} whileHover={{ y: -6 }}
              className="p-8 md:p-10 rounded-2xl transition-all duration-300"
              style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: "#fef8ec" }}>
                <Eye className="w-7 h-7 text-[#d4a73a]" weight="duotone" />
              </div>
              <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>Our Vision</h3>
              <p className="leading-relaxed" style={{ color: "#666" }}>
                To build a powerful ecosystem of trusted business owners who grow together through collaboration, accountability, and structured networking across every major city.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} whileHover={{ y: -6 }}
              className="p-8 md:p-10 rounded-2xl transition-all duration-300"
              style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: "#e8f5f0" }}>
                <Target className="w-7 h-7 text-[#1a8a6d]" weight="duotone" />
              </div>
              <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>Our Mission</h3>
              <div className="space-y-3">
                {[
                  "Create high-quality business chapters across regions",
                  "Enable consistent referral exchange between verified owners",
                  "Bring accountability and transparency into networking",
                  "Help members track and scale their business growth"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#1a8a6d] group-hover:scale-110 transition-transform" weight="fill" />
                    <span className="text-sm leading-relaxed" style={{ color: "#666" }}>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOUNDER & CORE TEAM */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="founder-team">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 text-center font-semibold" style={{ color: "#1a8a6d" }}>The People Behind BCH</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-center mb-8 sm:mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
              Founder & <span style={{ color: "#d4a73a" }}>Co Founder</span>
            </motion.h2>

            {/* Founder */}
            <motion.div variants={fadeUp} className="mb-10">
              <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-8 items-start p-5 sm:p-8 md:p-10 rounded-3xl"
                style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <div className="mx-auto lg:mx-0">
                  <div className="w-56 h-56 lg:w-64 lg:h-64 rounded-full overflow-hidden"
                    style={{ border: "4px solid #1a8a6d" }}
                    data-testid="founder-image-placeholder">
                    <img src="/bch_sir_new.png" alt="Kamal Agarwal" className="w-full h-full object-cover" style={{ objectPosition: "center -5%" }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase" style={{ background: "#e8f5f0", color: "#1a8a6d" }}>Founder</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>Kamal Agarwal</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: "#d4a73a" }}>Founder, Nayaa Modular Hardware & BCH</p>
                  <div className="space-y-3 text-sm leading-relaxed" style={{ color: "#555" }}>
                    <p>Kamal Agarwal has dedicated his journey to building businesses, empowering startups, and creating communities that empower entrepreneurs.</p>
                    <p>In 2019, he moved to Hyderabad with just 10 known contacts and a dream to grow his venture, Nayaa Modular Hardware Solutions, which he had started in 2013. New city, new language, and no network — but perseverance kept him going.</p>
                    <p>The turning point came when he explored a business networking platform for the first time. That single step transformed his journey, helping him build <span className="font-semibold text-[#1a8a6d]">25,000+ connections</span> and scale his business <span className="font-semibold text-[#1a8a6d]">110x</span> — from ₹50K in 2019 to multiple crores today.</p>
                    <p>Now, with BCH, his vision is to build a trusted community where entrepreneurs, especially startups, find the support, connections, and opportunities they need to grow.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Core Team Member */}
            <motion.div variants={fadeUp}>
              {/*<p className="text-xs tracking-[0.2em] uppercase mb-6 font-semibold" style={{ color: "#d4a73a" }}>Core Team</p>*/}
              <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-8 items-start p-5 sm:p-8 md:p-10 rounded-3xl"
                style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <div className="mx-auto lg:mx-0">
                  <div className="w-56 h-56 lg:w-64 lg:h-64 rounded-full overflow-hidden"
                    style={{ border: "4px solid #1a8a6d", background: "#fff" }}
                    data-testid="core-member-image-placeholder">
                    <img src="https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/hjvt3w3x_Screenshot_2026-04-09_at_10.36.22_AM-removebg-preview.png" alt="CA Varsha Gada" className="w-full h-full object-cover" style={{ transform: "scale(1.15) translateY(5%)" }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase" style={{ background: "#fef8ec", color: "#d4a73a" }}>Co founder</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>CA Varsha Gada</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: "#1a8a6d" }}>Partner at SKYZ & Co.</p>
                  <div className="space-y-3 text-sm leading-relaxed" style={{ color: "#555" }}>
                    <p>With 7 years of experience in Audit and Taxation for companies and individuals across industries such as Manufacturing and Software, Varsha has been supporting clients across India.</p>
                    <p>Being new to the city, she has established her own CA practice here and looks forward to connecting and exchanging ideas with like-minded professionals through this group.</p>
                    <p>Varsha believes BCH provides the right ecosystem for professionals to collaborate, share expertise, and grow together.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CHAPTER STRUCTURE — Horizontal image/text alternating */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="chapter-structure">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 text-center font-semibold" style={{ color: "#d4a73a" }}>The System</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight text-center" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
              A System That <span style={{ color: "#1a8a6d" }}>Drives Results</span>
            </motion.h2>

            <div className="relative mt-10">
              {/* Vertical line */}
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, transparent, #c5e8dc, #f0d9a0, transparent)" }} />

              {[
                { icon: Buildings, title: "Chapter-Based Networking", desc: "Organized local chapters with curated membership. Each chapter operates independently with its own leadership.", color: "#1a8a6d" },
                { icon: ShieldCheck, title: "Category Exclusivity", desc: "Only one business per category. Zero internal competition. Your market share is protected.", color: "#d4a73a" },
                { icon: Crown, title: "Leadership Roles", desc: "President, Vice President, and Secretary drive each chapter's success with accountability.", color: "#1a8a6d" },
                { icon: CalendarCheck, title: "Structured Meetings", desc: "Bi-weekly sessions designed for referral exchange, business pitches, and relationship building.", color: "#d4a73a" },
                { icon: ChartLineUp, title: "Performance Tracking", desc: "Every referral, attendance, and business outcome is tracked digitally for full transparency.", color: "#1a8a6d" },
                { icon: Rocket, title: "Measurable Growth", desc: "Real dashboards. Real metrics. See exactly how much business BCH has brought you.", color: "#d4a73a" }
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp}
                  className={`relative flex items-start gap-6 mb-8 last:mb-0 ${i % 2 !== 0 ? "md:flex-row-reverse md:text-right" : ""}`}>
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full z-10 mt-1"
                    style={{ background: item.color, boxShadow: `0 0 12px ${item.color}40` }} />
                  <div className="ml-14 md:ml-0 md:w-[calc(50%-40px)]" />
                  <motion.div whileHover={{ y: -4 }}
                    className="ml-14 md:ml-0 md:w-[calc(50%-40px)] p-6 rounded-2xl transition-all duration-300"
                    style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                    <item.icon className="w-7 h-7 mb-3" style={{ color: item.color }} weight="duotone" />
                    <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>{item.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "#777" }}>{item.desc}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* LEADERSHIP — with image */}
      <section className="py-10 md:py-14" style={{ background: "#f2f2ee" }} data-testid="leadership">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 text-center font-semibold" style={{ color: "#1a8a6d" }}>Governance</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight text-center mb-10" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
              Leadership That <span style={{ color: "#d4a73a" }}>Drives</span> Each Chapter
            </motion.h2>

            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <motion.div variants={scaleUp} className="rounded-3xl overflow-hidden shadow-xl aspect-[16/10] sm:aspect-[4/3]">
                <img src={IMAGES.conference} alt="Business conference" className="w-full h-full object-cover" loading="lazy" />
              </motion.div>
              <motion.div variants={stagger} className="space-y-5">
                {[
                  { role: "Chairman", desc: "Leads the chapter. Drives growth, facilitates meetings, and ensures accountability.", color: "#d4a73a", bg: "#fef8ec" },
                  { role: "Vice Chairman", desc: "Supports the President. Manages member relations and coordinates activities.", color: "#1a8a6d", bg: "#e8f5f0" },
                  { role: "Secretary Treasurer", desc: "Handles administration. Tracks attendance, minutes, and chapter communications.", color: "#6366f1", bg: "#eef2ff" }
                ].map((l, i) => (
                  <motion.div key={i} variants={fadeUp} whileHover={{ x: 8 }}
                    className="p-6 rounded-2xl transition-all duration-300 group"
                    style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                        style={{ background: l.bg }}>
                        <Crown className="w-6 h-6" style={{ color: l.color }} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: l.color }}>{l.role}</h3>
                        <p className="text-sm leading-relaxed" style={{ color: "#777" }}>{l.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="philosophy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 font-semibold" style={{ color: "#d4a73a" }}>Our Values</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight mb-10" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
              What We <span style={{ color: "#1a8a6d" }}>Believe</span> In
            </motion.h2>
            <motion.div variants={stagger} className="grid grid-cols-2 gap-5">
              {[
                { left: "Consistency", right: "randomness" },
                { left: "Quality", right: "quantity" },
                { left: "Relationships", right: "transactions" },
                { left: "Measurable growth", right: "assumptions" }
              ].map((p, i) => (
                <motion.div key={i} variants={fadeUp} whileHover={{ scale: 1.03 }}
                  className="p-6 rounded-2xl transition-all duration-300"
                  style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                  <p className="text-lg md:text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif", color: "#1a8a6d" }}>{p.left}</p>
                  <p className="text-sm mt-1" style={{ color: "#bbb" }}>over {p.right}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHO SHOULD JOIN — with image */}
      <section className="py-10 md:py-14" style={{ background: "#f2f2ee" }} data-testid="who-should-join">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
                Who Should <span style={{ color: "#d4a73a" }}>Join</span> BCH?
              </motion.h2>
              <motion.div variants={stagger} className="space-y-4">
                {[
                  "Business owners seeking structured growth",
                  "Service providers building long-term client pipelines",
                  "Professionals who value consistency & accountability",
                  "Entrepreneurs ready to invest in a trusted network"
                ].map((text, i) => (
                  <motion.div key={i} variants={fadeUp} whileHover={{ x: 6 }}
                    className="flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 group"
                    style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: "#fef8ec" }}>
                      <Handshake className="w-4 h-4 text-[#d4a73a]" weight="duotone" />
                    </div>
                    <span className="text-sm font-medium" style={{ color: "#555" }}>{text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
            <motion.div variants={scaleUp} className="rounded-3xl overflow-hidden shadow-xl aspect-[16/10] sm:aspect-[4/3]">
              <img src={IMAGES.collab} alt="Team collaboration" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 relative overflow-hidden" data-testid="about-cta"
        style={{ background: "linear-gradient(135deg, #1a8a6d 0%, #147058 100%)" }}>
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)`, backgroundSize: "60px 60px", height: "100%", width: "100%" }} />
        </div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-medium tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#fff" }}>
            Be Part of a Growth-Driven Network
          </motion.h2>
          <motion.p variants={fadeUp} className="text-base md:text-lg mb-10 leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
            Experience a structured way of growing your business through meaningful connections and verified professionals.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"
              className="group w-full sm:w-auto rounded-full px-10 py-4 font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-y-1 hover:shadow-2xl"
              style={{ background: "#d4a73a", color: "#111" }}>
              Apply for Membership <Lightning className="w-4 h-4 group-hover:rotate-12 transition-transform" weight="fill" />
            </Link>
            <Link to="/"
              className="w-full sm:w-auto rounded-full px-10 py-4 font-semibold text-sm transition-all duration-300 hover:-translate-y-1"
              style={{ border: "2px solid rgba(255,255,255,0.5)", color: "#fff" }}>
              Back to Home
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: "#fff", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src={LOGO_URL} alt="BCH" className="h-12 sm:h-16 w-auto" />
              <span className="text-xs sm:text-sm" style={{ color: "#999" }}>Connect &middot; Collaborate &middot; Grow</span>
            </div>
            <div className="flex items-center gap-6 sm:gap-8 text-xs sm:text-sm" style={{ color: "#777" }}>
              {["/", "/about", "/login", "/signup"].map((p, i) => (
                <Link key={p} to={p} className="hover:text-[#1a8a6d] transition-colors">{["Home", "About", "Login", "Apply"][i]}</Link>
              ))}
            </div>
          </div>
          <div className="mt-6 pt-4 text-center" style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <p className="text-xs" style={{ color: "#bbb" }}>&copy; 2026 BCH Networking. All rights reserved.</p>
            <p className="text-xs mt-2" style={{ color: "#ccc" }}>Website built with code, coffee, and a lot of logic by <a href="https://kensworth.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#1a8a6d] transition-colors" style={{ color: "#999", fontWeight: 600 }}>Kensworth.in</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
